import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type TextStyle,
} from 'react-native';

import { useReducedMotion } from '../useReducedMotion';

// One line of text that scrolls when it does not fit (#308, the logger card
// title). It stays still when it fits. When it does not: a pause at the start,
// a slide to the end, a pause, a slide back, THREE rounds, then it rests at the
// start. A text that moves for the whole rest distracts (Antenore, v3).
// Reduce motion: no scroll, the text wraps to two lines instead.
// The full text is always the caller's accessible name; this view is visual.

const ROUNDS = 3;
const PAUSE_MS = 1200;
const PX_PER_SEC = 30;
// Wide enough that the measuring row never wraps the text it measures.
const MEASURE_WIDTH = 4000;

export function MarqueeText({
  text,
  style,
}: {
  text: string;
  style?: StyleProp<TextStyle>;
}) {
  const reducedMotion = useReducedMotion();
  const [boxW, setBoxW] = useState(0);
  const [textW, setTextW] = useState(0);
  const shift = useRef(new Animated.Value(0)).current;
  const overflow = Math.max(0, Math.ceil(textW - boxW));

  useEffect(() => {
    shift.setValue(0);
    if (reducedMotion || overflow === 0 || boxW === 0) return;
    const slide = (toValue: number) =>
      Animated.timing(shift, {
        toValue,
        duration: (overflow / PX_PER_SEC) * 1000,
        // RNW has no native driver and warns when asked for one.
        useNativeDriver: Platform.OS !== 'web',
      });
    const run = Animated.loop(
      Animated.sequence([
        Animated.delay(PAUSE_MS),
        slide(-overflow),
        Animated.delay(PAUSE_MS),
        slide(0),
      ]),
      { iterations: ROUNDS },
    );
    run.start();
    return () => run.stop();
  }, [text, overflow, boxW, reducedMotion, shift]);

  if (reducedMotion) {
    return (
      <Text style={[styles.wrap, style]} numberOfLines={2}>
        {text}
      </Text>
    );
  }

  return (
    <View
      style={styles.box}
      onLayout={(e: LayoutChangeEvent) => setBoxW(e.nativeEvent.layout.width)}
    >
      <Animated.View
        style={[styles.track, { transform: [{ translateX: shift }] }]}
      >
        <Text
          style={style}
          numberOfLines={1}
          onLayout={(e: LayoutChangeEvent) =>
            setTextW(e.nativeEvent.layout.width)
          }
        >
          {text}
        </Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  box: { flex: 1, minWidth: 0, overflow: 'hidden' },
  wrap: { flex: 1, minWidth: 0 },
  track: { flexDirection: 'row', width: MEASURE_WIDTH },
});
